import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AlertDetail as AlertDetailT } from '../types';
import Code, { CodeText, kindForCode } from '../components/Code';
import DecisionPanel from '../components/DecisionPanel';
import ExplainThis from '../components/ExplainThis';
import ProvenanceChip from '../components/ProvenanceChip';
import { severityBand, SEVERITY_SCALE } from '../components/severity';

/**
 * /alert/:id — the cross-cutting alert resolution surface (handoffV5/V6 §15.2b).
 * Not a directory of links: it embeds a live decision brief with the one decision
 * model (six response options, derived disposition, single commit), and offers
 * jump-ins to the relevant sub-tabs for depth.
 */
export default function AlertDetail() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['alert', id], queryFn: () => api.alertDetail(id), retry: 0 });

  if (q.isLoading) return <div className="panel"><div className="small faint">Loading…</div></div>;
  if (q.isError || !q.data) {
    return (
      <div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="ph">Alert</div>
          <Link to="/" className="btn ghost">← Engine Room</Link>
        </div>
        <div className="panel"><div className="small" style={{ color: 'var(--red)' }}>No alert with that id — it may have been resolved.</div></div>
      </div>
    );
  }
  const d = q.data;
  const band = severityBand(d.severity);
  const [lo, hi] = d.consequence?.revenue_at_risk_usd_m ?? [0, 0];
  const signalId = d.machine_tag.split(' / ')[0] || 'S-02';

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 6 }}>
            <span className="tag" style={{ border: `1px solid ${band.color}`, color: band.color, background: 'transparent', fontSize: 11 }}>{band.label}</span>
            <span className="sev-scale">
              {SEVERITY_SCALE.map((b, i) => (
                <span key={b} className={b === band.label ? 'on' : ''}>{b}{i < SEVERITY_SCALE.length - 1 ? ' › ' : ''}</span>
              ))}
            </span>
            <span className="tiny faint"><CodeText text={d.machine_tag} compact /></span>
            <span className="small faint">owner {d.owner}</span>
          </div>
          <div className="h2" style={{ fontSize: 20, lineHeight: 1.35 }}>{d.headline_plain}</div>
        </div>
        <Link to="/" className="btn ghost" style={{ marginTop: 6 }}>← Engine Room</Link>
      </div>

      <div className="grid2" style={{ marginTop: 14 }}>
        {/* What it touches */}
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--red)' }} />What it touches</div>
          <ChainField label="Assumption" obj={d.chain?.assumption} />
          <ChainField label="Objective" obj={d.chain?.objective} />
          <ChainField label="Money node" obj={d.chain?.money_node} />
        </div>
        {/* Consequence */}
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />Consequence if no action</div>
          <div className="kpi" style={{ marginBottom: 8 }}>
            <div className="num" style={{ color: 'var(--red)' }}>−${lo}–{hi}M</div>
            <div className="lab">revenue at risk</div>
            <div style={{ marginTop: 6 }}><ProvenanceChip basis="modeled on a representative cost stack" /></div>
          </div>
          <div className="small" style={{ color: 'var(--muted)', lineHeight: 1.5 }}>{d.consequence?.text}</div>
        </div>
      </div>

      {/* Embedded decision brief */}
      <DecisionBrief brief={d.decision_brief} recommended={d.recommended_action?.text} />

      {/* 'Explain this in plain language' on the decision brief (spec 11.5; marked AI aid, the brief stays of record). */}
      {(() => {
        const eng = d.jump_ins?.[0]?.engine;
        return eng ? (
          <ExplainThis
            engine_id={eng}
            recommendation={d.decision_brief?.recommendation ?? d.recommended_action?.text ?? d.headline_plain}
            evidence={(d.decision_brief?.evidence ?? []).map((line) => ({ value: String(line) }))}
            tradeoffs={(d.decision_brief?.tradeoffs ?? []).map(String)}
            constraints={(d.decision_brief?.blocked ?? []).map(String)}
          />
        ) : null;
      })()}

      {/* Disposition — the one decision model */}
      <DecisionPanel
        owner={d.owner}
        commit={(disposition, envelope) =>
          api.simulationDisposition({
            simulation_kind: 'wargame',
            signal_id: signalId,
            disposition,
            owner: d.owner,
            recommendation: d.recommended_action?.text ?? d.headline_plain,
            decision: envelope,
          })
        }
      />

      {/* Jump-ins */}
      {d.jump_ins?.length > 0 && (
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Drill into the working surfaces</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {d.jump_ins.map((j, i) => {
              const params = new URLSearchParams();
              params.set('tab', j.tab);
              params.set('alert', d.id);
              if (j.scenario) params.set('scenario', j.scenario);
              return (
                <Link key={i} to={`/engine/${j.engine}?${params.toString()}`} className="btn ghost" style={{ textDecoration: 'none', fontSize: 13 }}>
                  {j.label} ↗
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ChainField({ label, obj }: { label: string; obj?: Record<string, unknown> }) {
  const id = obj?.id as string | undefined;
  const text = (obj?.statement ?? obj?.name ?? obj?.stage ?? '') as string;
  // A code is shown only when the registry recognizes it, and then as a faint secondary tag
  // (codeOnly). Objectives (O1..O6) are not in the code registry, so they render by name alone.
  return (
    <div className="field" style={{ borderBottom: '1px solid var(--line-soft)', padding: '8px 0' }}>
      <div className="field-label small faint">{label}</div>
      <div className="field-value" style={{ fontSize: 14 }}>
        {id && kindForCode(id) ? <span style={{ marginRight: 6 }}><Code id={id} kind={kindForCode(id)!} compact codeOnly /></span> : null}
        {text}
      </div>
    </div>
  );
}

function asText(x: unknown): string {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  if (typeof x === 'object') {
    const o = x as Record<string, unknown>;
    return (o.text ?? o.move ?? o.label ?? o.name ?? JSON.stringify(o)) as string;
  }
  return String(x);
}

function DecisionBrief({ brief, recommended }: { brief: AlertDetailT['decision_brief']; recommended?: string }) {
  if (!brief) return null;
  const evidence = (brief.evidence ?? []) as unknown[];
  const tradeoffs = (brief.tradeoffs ?? []) as unknown[];
  const blocked = (brief.blocked ?? []) as unknown[];
  const action = typeof brief.action_path === 'string' ? brief.action_path : asText(brief.action_path);
  return (
    <div className="panel" style={{ marginTop: 14, borderLeft: '3px solid var(--amber)' }}>
      <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Decision brief</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.35, marginBottom: 10 }}>{brief.recommendation || recommended}</div>
      {evidence.length > 0 && (
        <Section label="Evidence">{evidence.map((e, i) => <li key={i}><CodeText text={asText(e)} compact /></li>)}</Section>
      )}
      {tradeoffs.length > 0 && (
        <Section label="Trade-off">{tradeoffs.map((t, i) => <li key={i}><CodeText text={asText(t)} compact /></li>)}</Section>
      )}
      {blocked.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="small" style={{ color: 'var(--red)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Off the table</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{blocked.map((b, i) => <li key={i} style={{ color: 'var(--red)' }}><CodeText text={asText(b)} compact /></li>)}</ul>
        </div>
      )}
      {action && <div className="small" style={{ marginTop: 10, color: 'var(--muted)' }}><b>Action path:</b> <CodeText text={action} compact /></div>}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>{children}</ul>
    </div>
  );
}
